/**
 * §6.4 and §6.6 — the cash-basis profit and loss.
 *
 * WHAT THIS SCREEN IS FOR, WHICH IS NOT "SHOWING A NUMBER"
 *
 * The books are accrual and the tax returns are cash basis (§6). §6.1 is
 * explicit that you cannot get from one to the other by filtering on dates: an
 * invoice credits revenue with no cash date, the payment months later touches
 * no revenue account at all, so a cash-basis P&L built by filtering on
 * `cash_date` reports revenue of ZERO while every entry in it is correct. The
 * conversion at report time is the fix, and `convertToCashBasis` is it.
 *
 * That service was written, tested against a real database including §6.6's
 * reconciliation invariant, and then had no caller for an entire phase. This
 * screen and `GET /reports/cash-basis` are what make it reachable.
 *
 * THE RECONCILIATION IS SHOWN, NOT ASSUMED
 *
 * §6.6: net income must equal the net movement across bank accounts, less the
 * movements that are not P&L events — owner draws, loan principal, fixed-asset
 * purchases. The service returns all three numbers and this screen renders the
 * whole identity, including the arithmetic:
 *
 *     netIncome = bankMovement - nonPnlMovement
 *
 * Showing only net income would be the more obvious design and it would hide
 * exactly the failure the invariant exists to catch. A mutation that let bank
 * lines leak into the P&L passed every API test in the suite: the identity
 * still closed, because a stray bank line lands in the non-P&L bucket and net
 * income never moves. Two of the three numbers agreeing proves nothing. The
 * operator is shown all three and told whether they close.
 *
 * A REFUSAL HERE IS AN ANSWER, NOT AN ERROR
 *
 * `unapplied_control_line` means a deposit inside the period has not been
 * matched to an invoice. There is no originating document for the unmatched
 * part, so there is nothing to allocate it across, and a report produced
 * anyway would be short by exactly that amount with nothing raising anywhere.
 * The service refuses and says which entry to go and match. That sentence is
 * the most useful thing this screen can display, so it is displayed as
 * guidance with a link to the matching screen rather than as a red failure.
 *
 * NO DEFAULT PERIOD
 *
 * §6.9 makes the fiscal year a tenant setting. "This year" computed in the
 * browser would be a different claim for a tenant whose year does not start in
 * January, and a report is at its most dangerous when the numbers are right
 * for a period nobody chose. Both dates are typed.
 */

import { useState } from 'react';
import { ApiError, api, type CashBasisLine, type CashBasisReport } from '../api.js';
import { formatCents } from '../lib/money.js';
import { PrintButton, PrintHeading } from '../components/PrintReport.js';

/** A P&L account's own total, which is what a reader of a P&L wants. */
interface AccountTotal {
  accountId: string;
  code: string;
  name: string;
  type: string;
  /** `credit - debit`. Positive is revenue, negative is expense. */
  netCents: number;
  /** How many emitted lines rolled up here, for the drill-down. */
  lineCount: number;
  /** True when any contributing line was emitted by §6.4 step 3. */
  anyConverted: boolean;
}

/**
 * Roll the emitted lines up by account.
 *
 * The service returns one line per conversion, so a customer paying six
 * invoices in a period produces six lines against the same revenue account.
 * That is the right shape for auditing the conversion and the wrong shape for
 * reading a P&L, which is why both are available here rather than one of them
 * being chosen for the operator.
 *
 * Signed as `credit - debit` throughout, so revenue is positive and expense is
 * negative and the total is net income with no special-casing by type. Summing
 * absolute values and then deciding the sign from `type` is the version that
 * looks right and quietly mis-handles a refund posted as a debit to revenue.
 */
function rollUp(lines: CashBasisLine[]): AccountTotal[] {
  const byAccount = new Map<string, AccountTotal>();

  for (const line of lines) {
    const existing = byAccount.get(line.accountId);
    const net = line.creditCents - line.debitCents;

    if (existing) {
      existing.netCents += net;
      existing.lineCount += 1;
      existing.anyConverted = existing.anyConverted || line.converted;
    } else {
      byAccount.set(line.accountId, {
        accountId: line.accountId,
        code: line.accountCode,
        name: line.accountName,
        type: line.accountType,
        netCents: net,
        lineCount: 1,
        anyConverted: line.converted,
      });
    }
  }

  return [...byAccount.values()].sort((a, b) => a.code.localeCompare(b.code));
}

export function CashBasisPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [report, setReport] = useState<CashBasisReport | null>(null);
  const [loading, setLoading] = useState(false);

  /** A refusal the operator can act on. Not the same thing as a failure. */
  const [blocked, setBlocked] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showLines, setShowLines] = useState(false);

  async function run(event: React.FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setBlocked(null);

    try {
      setReport(await api.cashBasisReport(from, to));
    } catch (caught) {
      setReport(null);
      /*
       * `unapplied_control_line` and `nothing_to_allocate` are states of the
       * books, and the server's sentence names the entry to go and fix.
       * Everything else is a failure of the request.
       */
      if (
        caught instanceof ApiError &&
        (caught.code === 'unapplied_control_line' || caught.code === 'nothing_to_allocate')
      ) {
        setBlocked(caught.message);
      } else {
        setError(
          caught instanceof ApiError ? caught.message : 'Could not run the conversion.',
        );
      }
    } finally {
      setLoading(false);
    }
  }

  const totals = report ? rollUp(report.lines) : [];
  const revenue = totals.filter((total) => total.netCents > 0);
  const expense = totals.filter((total) => total.netCents <= 0);

  /*
   * §6.6, checked in the browser against the three numbers the server sent.
   *
   * This is not a second implementation of the conversion -- it is the
   * identity restated over the results, which is the only part a screen can
   * honestly verify. If it ever reads "does not close", the report is wrong
   * and the number on it must not be filed.
   */
  const closes =
    report !== null &&
    report.netIncomeCents === report.bankMovementCents - report.nonPnlMovementCents;

  return (
    <section className="report">
      {/*
        The period of the conversion that RAN, not of the boxes: they can be
        edited after a run, and they do not print.
      */}
      <PrintHeading covers={report === null ? undefined : `${report.from} to ${report.to}`} />
      <header className="row triage-head">
        <h1>Cash basis</h1>
        <span className="muted">§6.4 conversion · what the return reports</span>
        {/* Only once a conversion has run: before it there is nothing to print. */}
        {report !== null && <PrintButton />}
      </header>

      <form className="row triage-filters" onSubmit={run}>
        <label>
          From{' '}
          <input
            type="date"
            required
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </label>
        <label>
          To{' '}
          <input
            type="date"
            required
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </label>
        <button type="submit" disabled={loading || !from || !to}>
          {loading ? 'Converting…' : 'Run'}
        </button>
      </form>

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {/*
        A refusal, presented as the instruction it is. `role="status"` and not
        `alert`: nothing has gone wrong, there is something to do first.

        DELIBERATELY NOT A TOAST. Every other notice in this application now
        takes itself away after five seconds, because those are events that
        just happened. This is not an event -- it is the state of the books,
        it names the entry to go and match, and it carries the link to go and
        do it. Timing it out would take the instruction away from somebody
        reading it.
      */}
      {blocked && (
        <p role="status" className="notice">
          {blocked} <a href="/clearings">Go to matching</a>
        </p>
      )}

      {report && (
        <>
          <dl className="facts">
            <div className="fact">
              <dt>Net income</dt>
              <dd>{formatCents(report.netIncomeCents)}</dd>
            </div>
            <div className="fact">
              <dt>Bank movement</dt>
              <dd>{formatCents(report.bankMovementCents)}</dd>
            </div>
            <div className="fact">
              <dt>Not P&amp;L</dt>
              <dd>{formatCents(report.nonPnlMovementCents)}</dd>
            </div>
          </dl>

          {/*
            The whole identity, written out. §6.6 is the acceptance test for
            the conversion and this is it, evaluated against what was returned
            rather than against what the service believes it returned.
          */}
          <p className={closes ? 'rail rail-ok' : 'rail rail-off'} role="status">
            {closes
              ? `§6.6 closes: ${formatCents(report.netIncomeCents)} net income = ` +
                `${formatCents(report.bankMovementCents)} bank movement − ` +
                `${formatCents(report.nonPnlMovementCents)} not P&L.`
              : `§6.6 DOES NOT CLOSE. ${formatCents(report.netIncomeCents)} net income ` +
                `vs ${formatCents(
                  report.bankMovementCents - report.nonPnlMovementCents,
                )}. Do not file this. `}
          </p>

          {report.lines.length === 0 ? (
            <p className="queue-empty muted">
              No cash-basis activity between {report.from} and {report.to}.
            </p>
          ) : (
            <>
              <table className="lines">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Account</th>
                    <th className="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {revenue.map((total) => (
                    <tr key={total.accountId}>
                      <td>{total.code}</td>
                      <td>
                        {total.name}
                        {total.anyConverted && (
                          <span className="muted"> · converted</span>
                        )}
                      </td>
                      <td className="num">{formatCents(total.netCents)}</td>
                    </tr>
                  ))}
                  {expense.map((total) => (
                    <tr key={total.accountId}>
                      <td>{total.code}</td>
                      <td>
                        {total.name}
                        {total.anyConverted && (
                          <span className="muted"> · converted</span>
                        )}
                      </td>
                      <td className="num">{formatCents(total.netCents)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td />
                    <td>Net income</td>
                    <td className="num">{formatCents(report.netIncomeCents)}</td>
                  </tr>
                </tfoot>
              </table>

              {/*
                The emitted lines, behind a toggle.

                A converted line is not a line anyone posted: §6.4 step 3
                suppresses the control line and emits synthetic lines in its
                place, dated the CLEARING's cash date rather than the invoice's.
                An operator reconciling a number against the ledger will not
                find these, and telling them where each came from is the
                difference between a report they can check and one they must
                take on faith.
              */}
              <p>
                <button type="button" onClick={() => setShowLines(!showLines)}>
                  {showLines ? 'Hide' : 'Show'} the {report.lines.length} converted lines
                </button>
              </p>

              {showLines && (
                <table className="lines">
                  <thead>
                    <tr>
                      <th>Cash date</th>
                      <th>Account</th>
                      <th className="num">Debit</th>
                      <th className="num">Credit</th>
                      <th>From</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.lines.map((line, index) => (
                      <tr key={`${line.headerId}-${line.accountId}-${index}`}>
                        <td className="date-cell">{line.cashDate}</td>
                        <td>
                          {line.accountCode} {line.accountName}
                        </td>
                        <td className="num">
                          {line.debitCents ? formatCents(line.debitCents) : ''}
                        </td>
                        <td className="num">
                          {line.creditCents ? formatCents(line.creditCents) : ''}
                        </td>
                        <td>
                          {line.converted && line.sourceHeaderId ? (
                            <a href={`/journals/${line.sourceHeaderId}`}>the document</a>
                          ) : (
                            <a href={`/journals/${line.headerId}`}>the entry</a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
